import { useMemo } from "react";
import { ITheme, useTheme } from "../../other/theme";
import styled from "@emotion/styled";
import * as opacities from "../../other/theme/opacities";
import { Spinner } from "../spinner/Spinner";

type ButtonType = "success" | "danger" | "neutral" | "secondary";
type ButtonModifier = "normal" | "disabled" | "loading";

interface IProps {
    buttonType: ButtonType;
    modifier?: ButtonModifier /** defaults to normal */;
    children: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
    href?: string;
    target?: string;
    download?: boolean;
    testId: string;
}

export const Button = (props: IProps) => {
    const theme = useTheme();

    const isNormal =
        props.modifier === undefined || props.modifier === "normal";

    const propsOnClick = props.onClick;
    const onClick = useMemo(
        () => (event: React.MouseEvent<HTMLElement>) => {
            if (isNormal) {
                propsOnClick?.(event);
            }
        },
        [isNormal, propsOnClick]
    );

    const ContainerType =
        props.href && isNormal ? ContainerLink : ContainerButton;

    return (
        <ContainerType
            {...props}
            onClick={onClick}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            data-testid={props.testId}
            disabled={props.modifier === "disabled"}
            href={isNormal ? props.href : undefined}
            download={props.download}
        >
            <Content hide={props.modifier === "loading"}>
                {props.children}
            </Content>
            {props.modifier === "loading" && (
                <SpinnerWrapper>
                    <Spinner
                        size={20}
                        color={foregroundColorMapper({
                            ...props,
                            theme
                        })}
                    />
                </SpinnerWrapper>
            )}
        </ContainerType>
    );
};

const createContainer = (type: "button" | "a") =>
    styled(type)<IProps>(props => {
        const isNormal =
            props.modifier === undefined || props.modifier === "normal";

        return {
            cursor: isNormal ? "pointer" : "initial",
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            height: 30,
            padding: "0 20px",
            borderRadius: "3px",
            backgroundColor: backgroundColorMapper(props),
            color: foregroundColorMapper(props),
            opacity: opacityMapper(props),
            border:
                props.buttonType === "secondary"
                    ? "1px solid " + foregroundColorMapper(props)
                    : "none",
            boxShadow:
                "inset -1px -1px 0 0 " + props.theme.shadow + opacities.level20,
            fontSize: props.theme.fontSizes.medium,
            lineHeight: "16px",
            textDecoration: "none",
            "&:hover": {
                backgroundColor: isNormal
                    ? backgroundHoverColorMapper(props)
                    : undefined,
                opacity: isNormal
                    ? props.buttonType === "secondary"
                        ? 0.7
                        : 1
                    : undefined,
                color: foregroundColorMapper(props)
            }
        };
    });
const ContainerButton = createContainer("button");
const ContainerLink = createContainer("a");

const Content = styled("div")<{ hide: boolean }>(props => ({
    visibility: props.hide ? "hidden" : "visible"
}));

const SpinnerWrapper = styled("div")({
    position: "absolute",
    width: "100%",
    height: "100%",
    left: 0,
    top: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const opacityMapper = (props: IProps) => {
    if (props.modifier === "disabled") {
        if (props.buttonType === "secondary") {
            return 0.3;
        } else {
            return 0.55;
        }
    } else {
        return 1;
    }
};

const backgroundHoverColorMapper = (props: IProps & { theme: ITheme }) => {
    switch (props.buttonType) {
        case "neutral":
            return props.theme.neutral2;
        case "secondary":
            return "transparent";
        case "success":
            return props.theme.success2;
        case "danger":
            return props.theme.danger2;
        default:
            return props.theme.neutral2;
    }
};

const backgroundColorMapper = (props: IProps & { theme: ITheme }) => {
    switch (props.buttonType) {
        case "neutral":
            return props.theme.neutral1;
        case "secondary":
            return "transparent";
        case "success":
            return props.theme.success1;
        case "danger":
            return props.theme.danger1;
        default:
            return props.theme.neutral1;
    }
};

const foregroundColorMapper = (props: IProps & { theme: ITheme }) => {
    switch (props.buttonType) {
        case "neutral":
            return props.theme.neutralForeground;
        case "secondary":
            return props.theme.foregroundTitle;
        case "success":
            return props.theme.successForeground;
        case "danger":
            return props.theme.dangerForeground;
        default:
            return props.theme.neutralForeground;
    }
};
